import { Action, Dispatch } from 'redux';

import { activeLocaleInRedux } from '../../../localization';
import {
  AddLocation,
  LoadGeoIpLookup,
  SetClientAnalytics,
} from '../../../weborder/actions/Actions';
import { setupLanguage } from '../../../weborder/actions/loadingActions';
import * as analytics from '../../../weborder/analytics';
import { cookieConsent as cookieConsentPopup } from '../../../weborder/cookieconsent';
import { setCookieConsent } from '../../../weborder/redux/cookie-consent/actions';
import { State } from '../../../weborder/Store';
import { Utilities } from '../../../weborder/Utilities';
import { LocationCertainty } from '../../api/general/general.types';
import { getAppConfig } from '../../api/whitelabel/whitelabel.service';
import {
  AppConfig,
  ComplianceType,
} from '../../api/whitelabel/whitelabel.types';
import { analyticsService } from '../../services';
import { Async, AxiosError, Status } from '../../types/data.types';
import { SetAppConfig } from './whitelabel.types';
import { setAppConfig } from './whitelabelActions';

export const loadAppConfig =
  () =>
  async (dispatch: Dispatch<Action>, getState: () => State): Promise<void> => {
    const { locale } = getState();
    const { Offset: offset = 0 } = getState().flipdish;

    const dispatchSetAppConfig = (appConfig: Async<AppConfig>): SetAppConfig =>
      dispatch(setAppConfig(appConfig));

    dispatchSetAppConfig({ status: Status.LOADING });

    const dispatchThunk = dispatch as any;
    // TODO - ORD-59 refactor LoadGeoIpLookup and move to src/core/api/user/user.service.ts & src/core/state/user/userMiddleware.ts
    dispatchThunk(LoadGeoIpLookup());

    await getAppConfig()
      .then((data: AppConfig) => {
        // TODO - ORD-59 create util getPolicies and put in src/core/state/whitelabel/utils/whitelabelMiddlewareUtils.ts
        // #region - ORD-59 put into util file
        const CookiePolicyUrl =
          Utilities.extraOption('data-cookie-policy-link') ||
          data.CookiePolicyUrl ||
          'https://www.flipdish.com/cookie-policy/';
        const TermsAndConditionsUrl =
          Utilities.extraOption('data-terms-link') ||
          data.TermsAndConditionsUrl ||
          'https://www.flipdish.com/docs/tac/';
        const PrivacyPolicyUrl =
          Utilities.extraOption('data-privacy-policy-link') ||
          data.PrivacyPolicyUrl ||
          'https://www.flipdish.com/privacy-policy/';
        // #endregion

        const viewportTopOffset =
          data.DataOffset >= 0 ? data.DataOffset : offset;

        // TODO - ORD-59 create util setupLanguage and put in src/core/state/whitelabel/utils/whitelabelMiddlewareUtils.ts
        // #region - ORD-59 put into util file
        const appLanguage =
          data.Languages.length > 0 ? data.Languages[0]?.LangageId || '' : '';
        let activeLanguage = activeLocaleInRedux(locale.languages);

        const isAppLanguageAvailable =
          appLanguage !== '' && appLanguage !== activeLanguage;
        if (isAppLanguageAvailable) {
          activeLanguage = appLanguage;
          // TODO - ORD-59 refactor setupLanguage and move to src/core/state/general/generalMiddleware.ts
          dispatchThunk(setupLanguage(appLanguage));
        }
        // #endregion

        const customData = {
          ...data,
          CookiePolicyUrl,
          TermsAndConditionsUrl,
          PrivacyPolicyUrl,
          ViewportTopOffset: viewportTopOffset,
        };

        // TODO - ORD-59 refactor AddLocation and move to src/core/state/general/generalActions.ts
        dispatchThunk(
          AddLocation(
            {
              Latitude: customData.MapCenterLatitude,
              Longitude: customData.MapCenterLongitude,
            },
            LocationCertainty.AppConfig,
          ),
        );

        dispatchSetAppConfig({
          status: Status.SUCCESS,
          data: customData,
        });

        // TODO - ORD-59 create util to setupCookieConsent in src/core/state/whitelabel/utils/whitelabelMiddlewareUtils.ts
        // #region - ORD-59 put into util file
        const setupGA = (disable: boolean): void =>
          analytics.initialize(
            customData.GoogleAnalyticsTokens.map((trackingId) => [
              trackingId,
              {},
            ]),
            disable,
          );

        const isNonEssentialCookiesDisabled = customData.Features.includes(
          'disable-non-essential-cookies',
        );
        const isCookieConsentRequired =
          customData.ComplianceType === ComplianceType.GdprCompliance ||
          ('ComplianceType' in customData === false &&
            customData.Features.includes('enable-gdpr-cookies'));
        const analyticsSettings = {
          facebookIds: customData.FacebookTokens,
          uiVersion: customData.WebOrderUiVersion,
        };

        if (isNonEssentialCookiesDisabled) {
          setupGA(isNonEssentialCookiesDisabled);
        } else if (isCookieConsentRequired) {
          const restaurantName = customData.BrandName;
          const logoUrl = customData.BrandLogoUrl;

          cookieConsentPopup.init(
            {
              lang: activeLanguage,
              logoUrl,
              restaurantName,
              cookiePolicyUrl: customData.CookiePolicyUrl || '',
            },
            (userCookieSettings) => {
              const isPerformanceEnabled =
                userCookieSettings.includes('performance');

              analyticsService.initialise(
                analyticsSettings,
                userCookieSettings,
              );

              setupGA(!isPerformanceEnabled);

              // TODO - ORD-59 refactor setCookieConsent and move to src/core/state/general/generalActions.ts
              dispatch(
                setCookieConsent(
                  userCookieSettings as WebOrder.State.CookieConsent['selectedGroups'],
                ),
              );
            },
          );
        } else {
          analyticsService.initialise(analyticsSettings);
          setupGA(isNonEssentialCookiesDisabled);
        }
        // #endregion

        // If client analytics is not present it will still set ClientAnalytics
        // to true so GA sends event's to Flipdish's Google Analytics
        // TODO - ORD-59 refactor SetClientAnalytics (rename to setWhitelabelAnalytics) and move to src/core/state/whitelabel/whitelabelActions.ts
        dispatchThunk(SetClientAnalytics(true));
      })
      .catch((error: AxiosError) => {
        dispatchSetAppConfig({
          status: Status.ERROR,
          error,
        });
      });
  };
